// API Service for SaaS CRM
// Mock data and API calls - replace with real backend integration

// Types
export interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  status: 'new' | 'contacted' | 'qualified' | 'proposal' | 'won' | 'lost';
  source: string;
  value: number;
  assignedTo: string;
  createdAt: string;
  lastContact: string;
}

export interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  plan: 'starter' | 'professional' | 'enterprise';
  status: 'active' | 'inactive' | 'churned';
  revenue: number;
  joinedAt: string;
  lastOrder: string;
}

export interface Deal {
  id: string;
  title: string;
  value: number;
  stage: 'lead' | 'qualified' | 'proposal' | 'negotiation' | 'closed-won' | 'closed-lost';
  probability: number;
  lead: Lead;
  expectedClose: string;
  createdAt: string;
}

export interface DashboardStats {
  totalLeads: number;
  totalCustomers: number;
  revenue: number;
  conversionRate: number;
  leadsTrend: number;
  customersTrend: number;
  revenueTrend: number;
  conversionTrend: number;
}

export interface ChartData {
  month: string;
  revenue: number;
  leads: number;
}

// Admin Types
export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: string;
  status: 'active' | 'inactive';
  createdAt: string;
  lastLogin: string;
}

export interface Role {
  id: string;
  name: string;
  permissions: string[];
  usersCount: number;
}

export interface LeadStage {
  id: string;
  name: string;
  color: string;
  probability: number;
  order: number;
}

export interface EmailNotification {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  template: string;
}

export interface SystemSettings {
  companyName: string;
  timezone: string;
  currency: string;
  language: string;
  dateFormat: string;
}

// Mock Data
const mockLeads: Lead[] = [
  { id: '1', name: 'John Smith', email: 'john@techcorp.com', phone: '+1 234-567-8901', company: 'TechCorp Inc', status: 'qualified', source: 'Website', value: 15000, assignedTo: 'Sarah', createdAt: '2026-03-01', lastContact: '2026-03-10' },
  { id: '2', name: 'Emily Davis', email: 'emily@innovate.io', phone: '+1 234-567-8902', company: 'Innovate.io', status: 'new', source: 'Referral', value: 8000, assignedTo: 'Mike', createdAt: '2026-03-08', lastContact: '2026-03-08' },
  { id: '3', name: 'Michael Brown', email: 'michael@startup.co', phone: '+1 234-567-8903', company: 'Startup Co', status: 'proposal', source: 'LinkedIn', value: 25000, assignedTo: 'Sarah', createdAt: '2026-02-20', lastContact: '2026-03-09' },
  { id: '4', name: 'Lisa Wang', email: 'lisa@enterprise.com', phone: '+1 234-567-8904', company: 'Enterprise Ltd', status: 'contacted', source: 'Campaign', value: 50000, assignedTo: 'John', createdAt: '2026-03-05', lastContact: '2026-03-11' },
  { id: '5', name: 'David Johnson', email: 'david@smallbiz.net', phone: '+1 234-567-8905', company: 'SmallBiz Net', status: 'won', source: 'Website', value: 5000, assignedTo: 'Mike', createdAt: '2026-02-15', lastContact: '2026-03-07' },
  { id: '6', name: 'Amanda White', email: 'amanda@agency.com', phone: '+1 234-567-8906', company: 'Creative Agency', status: 'qualified', source: 'Referral', value: 12000, assignedTo: 'Sarah', createdAt: '2026-03-02', lastContact: '2026-03-10' },
];

const mockCustomers: Customer[] = [
  { id: '1', name: 'Acme Corporation', email: 'contact@acme.com', phone: '+1 111-111-1111', company: 'Acme Corp', plan: 'enterprise', status: 'active', revenue: 120000, joinedAt: '2025-06-15', lastOrder: '2026-02-28' },
  { id: '2', name: 'TechStart Inc', email: 'hello@techstart.io', phone: '+1 222-222-2222', company: 'TechStart', plan: 'professional', status: 'active', revenue: 24000, joinedAt: '2025-09-20', lastOrder: '2026-03-01' },
  { id: '3', name: 'Global Solutions', email: 'info@globalsol.com', phone: '+1 333-333-3333', company: 'Global Solutions', plan: 'enterprise', status: 'active', revenue: 180000, joinedAt: '2025-03-10', lastOrder: '2026-02-15' },
  { id: '4', name: 'Local Shop', email: 'owner@localshop.com', phone: '+1 444-444-4444', company: 'Local Shop', plan: 'starter', status: 'inactive', revenue: 2400, joinedAt: '2025-11-05', lastOrder: '2026-01-10' },
  { id: '5', name: 'Innovation Labs', email: 'team@innovlabs.co', phone: '+1 555-555-5555', company: 'Innovation Labs', plan: 'professional', status: 'active', revenue: 36000, joinedAt: '2025-08-22', lastOrder: '2026-02-20' },
];

const mockDeals: Deal[] = [
  { id: '1', title: 'TechCorp Enterprise Deal', value: 15000, stage: 'qualified', probability: 60, lead: mockLeads[0], expectedClose: '2026-04-15', createdAt: '2026-03-01' },
  { id: '2', title: 'Innovate.io Startup Package', value: 8000, stage: 'lead', probability: 20, lead: mockLeads[1], expectedClose: '2026-05-01', createdAt: '2026-03-08' },
  { id: '3', title: 'Startup Co Full Suite', value: 25000, stage: 'proposal', probability: 70, lead: mockLeads[2], expectedClose: '2026-03-30', createdAt: '2026-02-20' },
  { id: '4', title: 'Enterprise Ltd Contract', value: 50000, stage: 'negotiation', probability: 85, lead: mockLeads[3], expectedClose: '2026-03-25', createdAt: '2026-03-05' },
  { id: '5', title: 'SmallBiz Basic Plan', value: 5000, stage: 'closed-won', probability: 100, lead: mockLeads[4], expectedClose: '2026-03-07', createdAt: '2026-02-15' },
];

// API Functions
export const api = {
  // Dashboard
  getDashboardStats: async (): Promise<DashboardStats> => {
    await delay(300);
    return {
      totalLeads: 156,
      totalCustomers: 89,
      revenue: 458000,
      conversionRate: 23.5,
      leadsTrend: 12.5,
      customersTrend: 8.3,
      revenueTrend: 15.2,
      conversionTrend: -2.1,
    };
  },

  // Leads
  getLeads: async (): Promise<Lead[]> => {
    await delay(300);
    return mockLeads;
  },

  getLeadById: async (id: string): Promise<Lead | undefined> => {
    await delay(200);
    return mockLeads.find(lead => lead.id === id);
  },

  createLead: async (lead: Omit<Lead, 'id' | 'createdAt' | 'lastContact'>): Promise<Lead> => {
    await delay(300);
    const newLead: Lead = {
      ...lead,
      id: String(mockLeads.length + 1),
      createdAt: new Date().toISOString().split('T')[0],
      lastContact: new Date().toISOString().split('T')[0],
    };
    mockLeads.push(newLead);
    return newLead;
  },

  updateLead: async (id: string, updates: Partial<Lead>): Promise<Lead> => {
    await delay(300);
    const lead = mockLeads.find(l => l.id === id);
    if (!lead) throw new Error('Lead not found');
    return { ...lead, ...updates };
  },

  deleteLead: async (id: string): Promise<void> => {
    await delay(300);
    const index = mockLeads.findIndex(l => l.id === id);
    if (index === -1) throw new Error('Lead not found');
    mockLeads.splice(index, 1);
  },

  // Customers
  getCustomers: async (): Promise<Customer[]> => {
    await delay(300);
    return mockCustomers;
  },

  getCustomerById: async (id: string): Promise<Customer | undefined> => {
    await delay(200);
    return mockCustomers.find(c => c.id === id);
  },

  createCustomer: async (customer: Omit<Customer, 'id' | 'joinedAt' | 'lastOrder'>): Promise<Customer> => {
    await delay(300);
    const newCustomer: Customer = {
      ...customer,
      id: String(mockCustomers.length + 1),
      joinedAt: new Date().toISOString().split('T')[0],
      lastOrder: new Date().toISOString().split('T')[0],
    };
    mockCustomers.push(newCustomer);
    return newCustomer;
  },

  updateCustomer: async (id: string, updates: Partial<Customer>): Promise<Customer> => {
    await delay(300);
    const customer = mockCustomers.find(c => c.id === id);
    if (!customer) throw new Error('Customer not found');
    return { ...customer, ...updates };
  },

  deleteCustomer: async (id: string): Promise<void> => {
    await delay(300);
    const index = mockCustomers.findIndex(c => c.id === id);
    if (index === -1) throw new Error('Customer not found');
    mockCustomers.splice(index, 1);
  },

  // Deals / Pipeline
  getDeals: async (): Promise<Deal[]> => {
    await delay(300);
    return mockDeals;
  },

  createDeal: async (deal: Omit<Deal, 'id' | 'createdAt'>): Promise<Deal> => {
    await delay(300);
    const newDeal: Deal = {
      ...deal,
      id: String(mockDeals.length + 1),
      createdAt: new Date().toISOString().split('T')[0],
    };
    mockDeals.push(newDeal);
    return newDeal;
  },

  updateDeal: async (id: string, updates: Partial<Deal>): Promise<Deal> => {
    await delay(300);
    const deal = mockDeals.find(d => d.id === id);
    if (!deal) throw new Error('Deal not found');
    return { ...deal, ...updates };
  },

  deleteDeal: async (id: string): Promise<void> => {
    await delay(300);
    const index = mockDeals.findIndex(d => d.id === id);
    if (index === -1) throw new Error('Deal not found');
    mockDeals.splice(index, 1);
  },

  // Revenue data for charts
  getRevenueData: async () => {
    await delay(200);
    return [
      { month: 'Oct', revenue: 32000, leads: 45 },
      { month: 'Nov', revenue: 38000, leads: 52 },
      { month: 'Dec', revenue: 41000, leads: 48 },
      { month: 'Jan', revenue: 35000, leads: 61 },
      { month: 'Feb', revenue: 48000, leads: 72 },
      { month: 'Mar', revenue: 52000, leads: 85 },
    ];
  },

  // Admin - Users
  getUsers: async (): Promise<AdminUser[]> => {
    await delay(300);
    return mockUsers;
  },

  createUser: async (user: Omit<AdminUser, 'id' | 'createdAt' | 'lastLogin'>): Promise<AdminUser> => {
    await delay(300);
    const newUser: AdminUser = {
      ...user,
      id: String(mockUsers.length + 1),
      createdAt: new Date().toISOString().split('T')[0],
      lastLogin: new Date().toISOString().split('T')[0],
    };
    mockUsers.push(newUser);
    return newUser;
  },

  updateUser: async (id: string, updates: Partial<AdminUser>): Promise<AdminUser> => {
    await delay(300);
    const user = mockUsers.find(u => u.id === id);
    if (!user) throw new Error('User not found');
    return { ...user, ...updates };
  },

  deleteUser: async (id: string): Promise<void> => {
    await delay(300);
    const index = mockUsers.findIndex(u => u.id === id);
    if (index === -1) throw new Error('User not found');
    mockUsers.splice(index, 1);
  },

  // Admin - Roles
  getRoles: async (): Promise<Role[]> => {
    await delay(300);
    return mockRoles;
  },

  createRole: async (role: Omit<Role, 'id' | 'usersCount'>): Promise<Role> => {
    await delay(300);
    const newRole: Role = {
      ...role,
      id: String(mockRoles.length + 1),
      usersCount: 0,
    };
    mockRoles.push(newRole);
    return newRole;
  },

  updateRole: async (id: string, updates: Partial<Role>): Promise<Role> => {
    await delay(300);
    const role = mockRoles.find(r => r.id === id);
    if (!role) throw new Error('Role not found');
    return { ...role, ...updates };
  },

  deleteRole: async (id: string): Promise<void> => {
    await delay(300);
    const index = mockRoles.findIndex(r => r.id === id);
    if (index === -1) throw new Error('Role not found');
    mockRoles.splice(index, 1);
  },

  // Admin - Lead Stages
  getLeadStages: async (): Promise<LeadStage[]> => {
    await delay(300);
    return mockLeadStages;
  },

  updateLeadStage: async (id: string, updates: Partial<LeadStage>): Promise<LeadStage> => {
    await delay(300);
    const stage = mockLeadStages.find(s => s.id === id);
    if (!stage) throw new Error('Lead stage not found');
    return { ...stage, ...updates };
  },

  createLeadStage: async (stage: Omit<LeadStage, 'id'>): Promise<LeadStage> => {
    await delay(300);
    const newStage: LeadStage = {
      ...stage,
      id: String(mockLeadStages.length + 1),
    };
    mockLeadStages.push(newStage);
    return newStage;
  },

  deleteLeadStage: async (id: string): Promise<void> => {
    await delay(300);
    const index = mockLeadStages.findIndex(s => s.id === id);
    if (index === -1) throw new Error('Lead stage not found');
    mockLeadStages.splice(index, 1);
  },

  // Admin - Email Notifications
  getEmailNotifications: async (): Promise<EmailNotification[]> => {
    await delay(300);
    return mockEmailNotifications;
  },

  updateEmailNotification: async (id: string, updates: Partial<EmailNotification>): Promise<EmailNotification> => {
    await delay(300);
    const notification = mockEmailNotifications.find(n => n.id === id);
    if (!notification) throw new Error('Notification not found');
    return { ...notification, ...updates };
  },

  // Admin - System Settings
  getSystemSettings: async (): Promise<SystemSettings> => {
    await delay(200);
    return mockSystemSettings;
  },

  updateSystemSettings: async (updates: Partial<SystemSettings>): Promise<SystemSettings> => {
    await delay(300);
    Object.assign(mockSystemSettings, updates);
    return mockSystemSettings;
  },
};

// Admin Mock Data
let mockUsers: AdminUser[] = [
  { id: '1', name: 'John Doe', email: 'john@company.com', role: 'Admin', status: 'active', createdAt: '2025-01-15', lastLogin: '2026-03-13' },
  { id: '2', name: 'Sarah Wilson', email: 'sarah@company.com', role: 'Sales Manager', status: 'active', createdAt: '2025-03-20', lastLogin: '2026-03-12' },
  { id: '3', name: 'Mike Johnson', email: 'mike@company.com', role: 'Sales Rep', status: 'active', createdAt: '2025-06-10', lastLogin: '2026-03-11' },
  { id: '4', name: 'Emily Davis', email: 'emily@company.com', role: 'Sales Rep', status: 'inactive', createdAt: '2025-08-05', lastLogin: '2026-02-28' },
  { id: '5', name: 'Alex Chen', email: 'alex@company.com', role: 'Viewer', status: 'active', createdAt: '2025-11-12', lastLogin: '2026-03-10' },
];

let mockRoles: Role[] = [
  { id: '1', name: 'Admin', permissions: ['all'], usersCount: 1 },
  { id: '2', name: 'Sales Manager', permissions: ['leads.manage', 'deals.manage', 'customers.view', 'reports.view'], usersCount: 1 },
  { id: '3', name: 'Sales Rep', permissions: ['leads.manage', 'deals.own', 'customers.view'], usersCount: 2 },
  { id: '4', name: 'Viewer', permissions: ['leads.view', 'deals.view', 'customers.view'], usersCount: 1 },
];

let mockLeadStages: LeadStage[] = [
  { id: '1', name: 'New', color: '#3b82f6', probability: 10, order: 1 },
  { id: '2', name: 'Contacted', color: '#f59e0b', probability: 25, order: 2 },
  { id: '3', name: 'Qualified', color: '#8b5cf6', probability: 50, order: 3 },
  { id: '4', name: 'Proposal', color: '#06b6d4', probability: 65, order: 4 },
  { id: '5', name: 'Won', color: '#22c55e', probability: 100, order: 5 },
  { id: '6', name: 'Lost', color: '#ef4444', probability: 0, order: 6 },
];

let mockEmailNotifications: EmailNotification[] = [
  { id: '1', name: 'New Lead Assigned', description: 'Notify when a new lead is assigned to a user', enabled: true, template: 'Hello {{user}}, a new lead {{lead_name}} has been assigned to you.' },
  { id: '2', name: 'Deal Stage Changed', description: 'Notify when a deal moves to a new stage', enabled: true, template: 'Deal "{{deal_title}}" has moved to {{stage}}.' },
  { id: '3', name: 'Lead Follow-up Reminder', description: 'Daily reminder for leads needing follow-up', enabled: true, template: 'You have {{count}} leads requiring follow-up today.' },
  { id: '4', name: 'Weekly Summary', description: 'Weekly summary of sales performance', enabled: false, template: 'Your weekly summary: {{metrics}}' },
  { id: '5', name: 'Customer Churn Risk', description: 'Alert when customer shows churn signals', enabled: true, template: 'Customer {{customer}} is showing churn risk signals.' },
];

let mockSystemSettings: SystemSettings = {
  companyName: 'SalesCRM',
  timezone: 'IST',
  currency: 'INR',
  language: 'en',
  dateFormat: 'DD-MM-YYYY',
};

// Helper function to simulate API delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export default api;
